import { useState, useEffect, useCallback, useMemo } from "react";
import { supabase } from "@/lib/supabase";
import { useStoreSettings } from "@/hooks/useStoreSettings";

interface Notification {
  id: number;
  product_id: number;
  product_name: string;
  phone: string;
  whatsapp: string;
  created_at: string;
  notified_at: string | null;
  status: "pending" | "notified" | "cancelled";
}

interface GroupedProduct {
  product_id: number;
  product_name: string;
  subscribers: Notification[];
}

/* ── Demand Report Item ── */
interface DemandItem {
  product_id: number;
  product_name: string;
  pendingCount: number;
  totalCount: number;
  notifiedCount: number;
  cancelledCount: number;
  latestRequest: string;
  urgency: "critical" | "high" | "medium" | "low";
}

function getUrgency(pending: number): DemandItem["urgency"] {
  if (pending >= 10) return "critical";
  if (pending >= 5) return "high";
  if (pending >= 2) return "medium";
  return "low";
}

const URGENCY_CONFIG = {
  critical: {
    label: "عاجل جداً",
    color: "text-red-400",
    bg: "bg-red-500/10",
    border: "border-red-500/25",
    bar: "#ef4444",
    dot: "bg-red-400 animate-ping",
    icon: "ri-alarm-warning-fill",
    priority: "أعد التخزين فوراً",
  },
  high: {
    label: "طلب مرتفع",
    color: "text-orange-400",
    bg: "bg-orange-500/10",
    border: "border-orange-500/25",
    bar: "#f97316",
    dot: "bg-orange-400 animate-pulse",
    icon: "ri-arrow-up-circle-fill",
    priority: "أولوية عالية للتخزين",
  },
  medium: {
    label: "طلب متوسط",
    color: "text-amber-400",
    bg: "bg-amber-400/10",
    border: "border-amber-400/25",
    bar: "#fbbf24",
    dot: "bg-amber-400",
    icon: "ri-time-fill",
    priority: "يُنصح بإعادة التخزين",
  },
  low: {
    label: "طلب منخفض",
    color: "text-gray-400",
    bg: "bg-white/5",
    border: "border-white/10",
    bar: "#6b7280",
    dot: "bg-gray-500",
    icon: "ri-information-line",
    priority: "مراقبة الطلب",
  },
};

/* ── Demand Report View ── */
function DemandReport({ notifications }: { notifications: Notification[] }) {
  const demandList = useMemo<DemandItem[]>(() => {
    const map: Record<number, DemandItem> = {};
    notifications.forEach((n) => {
      if (!map[n.product_id]) {
        map[n.product_id] = {
          product_id: n.product_id,
          product_name: n.product_name,
          pendingCount: 0,
          totalCount: 0,
          notifiedCount: 0,
          cancelledCount: 0,
          latestRequest: n.created_at,
          urgency: "low",
        };
      }
      map[n.product_id].totalCount++;
      if (n.status === "pending") map[n.product_id].pendingCount++;
      if (n.status === "notified") map[n.product_id].notifiedCount++;
      if (n.status === "cancelled") map[n.product_id].cancelledCount++;
      if (new Date(n.created_at) > new Date(map[n.product_id].latestRequest)) {
        map[n.product_id].latestRequest = n.created_at;
      }
    });

    return Object.values(map)
      .map((item) => ({ ...item, urgency: getUrgency(item.pendingCount) }))
      .sort((a, b) => b.pendingCount - a.pendingCount);
  }, [notifications]);

  const maxPending = Math.max(...demandList.map((d) => d.pendingCount), 1);

  const totalPending = demandList.reduce((s, d) => s + d.pendingCount, 0);
  const criticalCount = demandList.filter((d) => d.urgency === "critical").length;
  const highCount = demandList.filter((d) => d.urgency === "high").length;

  if (demandList.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center py-20 text-center">
        <div className="w-16 h-16 flex items-center justify-center bg-[#1a1a1a] rounded-full mb-4">
          <i className="ri-bar-chart-2-line text-2xl text-gray-600"></i>
        </div>
        <p className="text-gray-500 text-sm" style={{ fontFamily: "'Cairo', sans-serif" }}>
          لا توجد بيانات كافية لعرض التقرير
        </p>
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-5">

      {/* Summary KPIs */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        {[
          { label: "منتجات مطلوبة", value: demandList.length, icon: "ri-box-3-line", color: "text-white" },
          { label: "إجمالي الطلبات المعلقة", value: totalPending, icon: "ri-time-line", color: "text-amber-400" },
          { label: "عاجل جداً", value: criticalCount, icon: "ri-alarm-warning-fill", color: "text-red-400" },
          { label: "طلب مرتفع", value: highCount, icon: "ri-arrow-up-circle-fill", color: "text-orange-400" },
        ].map((s) => (
          <div key={s.label} className="bg-[#111] border border-white/5 rounded-2xl px-4 py-4 flex items-center gap-3">
            <div className="w-10 h-10 flex items-center justify-center bg-white/5 rounded-xl flex-shrink-0">
              <i className={`${s.icon} text-lg ${s.color}`}></i>
            </div>
            <div>
              <p className={`text-xl font-black ${s.color}`} style={{ fontFamily: "'Cairo', sans-serif" }}>{s.value}</p>
              <p className="text-gray-600 text-xs leading-tight" style={{ fontFamily: "'Cairo', sans-serif" }}>{s.label}</p>
            </div>
          </div>
        ))}
      </div>

      {/* Priority Legend */}
      <div className="flex flex-wrap gap-2">
        {(Object.entries(URGENCY_CONFIG) as [DemandItem["urgency"], typeof URGENCY_CONFIG.critical][]).map(([key, cfg]) => (
          <div key={key} className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full border text-xs font-bold ${cfg.bg} ${cfg.border} ${cfg.color}`}
            style={{ fontFamily: "'Cairo', sans-serif" }}>
            <span className={`w-1.5 h-1.5 rounded-full flex-shrink-0 ${cfg.dot.replace("animate-ping", "").replace("animate-pulse", "")}`}></span>
            {cfg.label}
          </div>
        ))}
      </div>

      {/* Ranked List */}
      <div className="flex flex-col gap-3">
        {demandList.map((item, idx) => {
          const cfg = URGENCY_CONFIG[item.urgency];
          const barPct = Math.round((item.pendingCount / maxPending) * 100);
          const timeAgo = (() => {
            const diff = Date.now() - new Date(item.latestRequest).getTime();
            const h = Math.floor(diff / 3600000);
            const d = Math.floor(h / 24);
            if (d > 0) return `منذ ${d} يوم`;
            if (h > 0) return `منذ ${h} ساعة`;
            return "منذ قليل";
          })();

          return (
            <div
              key={item.product_id}
              className={`rounded-2xl border overflow-hidden transition-all ${cfg.bg} ${cfg.border}`}
            >
              <div className="px-5 py-4 flex flex-col sm:flex-row sm:items-center gap-4">
                {/* Rank */}
                <div className="flex items-center gap-3 flex-shrink-0">
                  <div className={`w-9 h-9 flex items-center justify-center rounded-xl font-black text-sm flex-shrink-0 ${
                    idx === 0 ? "bg-amber-400 text-black" :
                    idx === 1 ? "bg-gray-300 text-black" :
                    idx === 2 ? "bg-orange-400 text-black" :
                    "bg-white/10 text-gray-400"
                  }`} style={{ fontFamily: "'Cairo', sans-serif" }}>
                    {idx + 1}
                  </div>
                </div>

                {/* Product Info */}
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap mb-1">
                    <p className="text-white font-black text-sm truncate" style={{ fontFamily: "'Cairo', sans-serif" }}>
                      {item.product_name}
                    </p>
                    <span className={`flex items-center gap-1 text-[10px] font-bold px-2 py-0.5 rounded-full border ${cfg.bg} ${cfg.border} ${cfg.color} whitespace-nowrap`}
                      style={{ fontFamily: "'Cairo', sans-serif" }}>
                      <i className={`${cfg.icon} text-[10px]`}></i>
                      {cfg.label}
                    </span>
                  </div>

                  {/* Bar */}
                  <div className="flex items-center gap-2 mb-2">
                    <div className="flex-1 h-2 bg-white/5 rounded-full overflow-hidden">
                      <div
                        className="h-full rounded-full transition-all duration-700"
                        style={{ width: `${barPct}%`, background: cfg.bar }}
                      ></div>
                    </div>
                    <span className="text-[11px] text-gray-500 flex-shrink-0" style={{ fontFamily: "'Cairo', sans-serif" }}>
                      {barPct}%
                    </span>
                  </div>

                  {/* Meta */}
                  <div className="flex flex-wrap gap-3 text-[11px] text-gray-500" style={{ fontFamily: "'Cairo', sans-serif" }}>
                    <span className="flex items-center gap-1">
                      <i className="ri-time-line text-[10px]"></i>
                      آخر طلب: {timeAgo}
                    </span>
                    {item.notifiedCount > 0 && (
                      <span className="flex items-center gap-1 text-emerald-400/70">
                        <i className="ri-checkbox-circle-line text-[10px]"></i>
                        أُشعر {item.notifiedCount}
                      </span>
                    )}
                    {item.cancelledCount > 0 && (
                      <span className="flex items-center gap-1 text-gray-600">
                        <i className="ri-close-circle-line text-[10px]"></i>
                        ألغى {item.cancelledCount}
                      </span>
                    )}
                  </div>
                </div>

                {/* Stats + Recommendation */}
                <div className="flex flex-col items-end gap-2 flex-shrink-0">
                  {/* Pending count */}
                  <div className="text-center">
                    <p className={`text-2xl font-black leading-none ${cfg.color}`} style={{ fontFamily: "'Cairo', sans-serif" }}>
                      {item.pendingCount}
                    </p>
                    <p className="text-gray-600 text-[10px] mt-0.5" style={{ fontFamily: "'Cairo', sans-serif" }}>
                      بانتظار الإشعار
                    </p>
                  </div>

                  {/* Recommendation chip */}
                  <div className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full border text-[10px] font-bold whitespace-nowrap ${cfg.bg} ${cfg.border} ${cfg.color}`}
                    style={{ fontFamily: "'Cairo', sans-serif" }}>
                    <i className="ri-store-2-line text-[10px]"></i>
                    {cfg.priority}
                  </div>

                  {/* Total */}
                  <p className="text-gray-600 text-[10px]" style={{ fontFamily: "'Cairo', sans-serif" }}>
                    إجمالي الطلبات: {item.totalCount}
                  </p>
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {/* Export hint */}
      <div className="flex items-center gap-2 bg-white/3 border border-white/8 rounded-xl px-4 py-3">
        <i className="ri-lightbulb-line text-amber-400 text-sm flex-shrink-0"></i>
        <p className="text-gray-500 text-xs leading-relaxed" style={{ fontFamily: "'Cairo', sans-serif" }}>
          <span className="text-white font-bold">توصية: </span>
          ابدأ بإعادة تخزين المنتجات ذات الأولوية العاجلة أولاً، ثم أرسل إشعارات واتساب للمشتركين فور توفرها.
        </p>
      </div>
    </div>
  );
}

/* ══════════════════════════════════════════════════════════ */
/*               BULK NOTIFY MODAL                           */
/* ══════════════════════════════════════════════════════════ */

interface BulkNotifyModalProps {
  allPendingNotifications: Notification[];
  storeName: string;
  onClose: () => void;
  onDone: (count: number) => void;
  onUpdateStatus: (ids: number[]) => void;
}

const DEFAULT_MSG_TEMPLATE = (storeName: string) =>
  `🔔 *مرحباً من ${storeName}!*\n\nنود إعلامك بأن المنتج الذي طلبت الإشعار عنه أصبح متوفراً الآن.\n\nسارع بطلبه قبل نفاده!\n\n— ${storeName}`;

function BulkNotifyModal({
  allPendingNotifications,
  storeName,
  onClose,
  onDone,
  onUpdateStatus,
}: BulkNotifyModalProps) {
  const [msgTemplate, setMsgTemplate] = useState(DEFAULT_MSG_TEMPLATE(storeName));
  const [selectedProductId, setSelectedProductId] = useState<number | "all">("all");
  const [sending, setSending] = useState(false);
  const [sentCount, setSentCount] = useState(0);
  const [done, setDone] = useState(false);
  const [currentIdx, setCurrentIdx] = useState(-1);

  // Group pending by product
  const productGroups = useMemo(() => {
    const map: Record<number, { product_id: number; product_name: string; count: number }> = {};
    allPendingNotifications.forEach((n) => {
      if (!map[n.product_id]) {
        map[n.product_id] = { product_id: n.product_id, product_name: n.product_name, count: 0 };
      }
      map[n.product_id].count++;
    });
    return Object.values(map).sort((a, b) => b.count - a.count);
  }, [allPendingNotifications]);

  const targets = selectedProductId === "all"
    ? allPendingNotifications
    : allPendingNotifications.filter((n) => n.product_id === selectedProductId);

  const handleSend = async () => {
    if (targets.length === 0 || !msgTemplate.trim()) return;
    setSending(true);
    const notifiedIds: number[] = [];

    for (let i = 0; i < targets.length; i++) {
      const notif = targets[i];
      setCurrentIdx(i);

      // Build per-subscriber message (include product name + link)
      const personalMsg = msgTemplate
        + `\n\n📦 *${notif.product_name}*`
        + `\n🔗 ${window.location.origin}/product/${notif.product_id}`;

      const phone = (notif.whatsapp || notif.phone).replace(/^0/, "966");
      window.open(
        `https://wa.me/${phone}?text=${encodeURIComponent(personalMsg)}`,
        "_blank",
        "noopener,noreferrer"
      );

      await supabase
        .from("stock_notifications")
        .update({ status: "notified", notified_at: new Date().toISOString() })
        .eq("id", notif.id);

      notifiedIds.push(notif.id);
      setSentCount(i + 1);

      if (i < targets.length - 1) {
        await new Promise((r) => setTimeout(r, 800));
      }
    }

    onUpdateStatus(notifiedIds);
    setSending(false);
    setDone(true);
    onDone(notifiedIds.length);
  };

  const charCount = msgTemplate.length;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60" dir="rtl">
      <div className="bg-[#1a1a1a] border border-white/10 rounded-2xl w-full max-w-lg overflow-hidden flex flex-col max-h-[90vh]">

        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-white/8 flex-shrink-0">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 flex items-center justify-center bg-emerald-500/15 border border-emerald-500/25 rounded-xl flex-shrink-0">
              <i className="ri-send-plane-fill text-emerald-400 text-base"></i>
            </div>
            <div>
              <h2 className="text-white font-black text-base" style={{ fontFamily: "'Cairo', sans-serif" }}>
                إشعار جماعي مخصص
              </h2>
              <p className="text-gray-500 text-xs mt-0.5" style={{ fontFamily: "'Cairo', sans-serif" }}>
                أرسل رسالة واتساب لكل المشتركين المعلقين
              </p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-white/8 text-gray-500 hover:text-white transition-colors cursor-pointer"
          >
            <i className="ri-close-line text-base"></i>
          </button>
        </div>

        <div className="flex-1 overflow-y-auto px-6 py-5 flex flex-col gap-5">
          {done ? (
            /* ── Success ── */
            <div className="flex flex-col items-center py-10 text-center">
              <div className="w-20 h-20 flex items-center justify-center bg-emerald-500/15 border border-emerald-500/25 rounded-full mb-5">
                <i className="ri-checkbox-circle-fill text-emerald-400 text-4xl"></i>
              </div>
              <p className="text-white font-black text-xl mb-2" style={{ fontFamily: "'Cairo', sans-serif" }}>
                تم الإرسال بنجاح!
              </p>
              <p className="text-gray-400 text-sm" style={{ fontFamily: "'Cairo', sans-serif" }}>
                تم إشعار <span className="text-emerald-400 font-black">{sentCount}</span> مشترك عبر واتساب
              </p>
            </div>
          ) : (
            <>
              {/* ── Product Filter ── */}
              <div>
                <label className="block text-xs font-bold text-gray-400 mb-2" style={{ fontFamily: "'Cairo', sans-serif" }}>
                  إرسال إلى
                </label>
                <div className="flex flex-wrap gap-2">
                  <button
                    onClick={() => setSelectedProductId("all")}
                    disabled={sending}
                    className={`flex items-center gap-1.5 px-3 py-2 rounded-xl text-xs font-bold border transition-all cursor-pointer whitespace-nowrap ${
                      selectedProductId === "all"
                        ? "bg-white text-[#1a1a1a] border-white"
                        : "bg-white/5 border-white/10 text-gray-400 hover:text-white"
                    }`}
                    style={{ fontFamily: "'Cairo', sans-serif" }}
                  >
                    <i className="ri-group-line text-xs"></i>
                    الكل ({allPendingNotifications.length})
                  </button>
                  {productGroups.map((g) => (
                    <button
                      key={g.product_id}
                      onClick={() => setSelectedProductId(g.product_id)}
                      disabled={sending}
                      className={`flex items-center gap-1.5 px-3 py-2 rounded-xl text-xs font-bold border transition-all cursor-pointer whitespace-nowrap ${
                        selectedProductId === g.product_id
                          ? "bg-emerald-500/20 border-emerald-500/40 text-emerald-300"
                          : "bg-white/5 border-white/10 text-gray-400 hover:text-white"
                      }`}
                      style={{ fontFamily: "'Cairo', sans-serif" }}
                    >
                      <i className="ri-box-3-line text-xs"></i>
                      {g.product_name.length > 20 ? g.product_name.slice(0, 20) + "…" : g.product_name}
                      <span className="bg-white/10 px-1.5 py-0.5 rounded-full text-[10px]">{g.count}</span>
                    </button>
                  ))}
                </div>
              </div>

              {/* ── Recipients Preview ── */}
              <div className="bg-[#111] border border-white/5 rounded-xl px-4 py-3 flex items-center gap-3">
                <div className="w-9 h-9 flex items-center justify-center bg-amber-400/10 border border-amber-400/20 rounded-lg flex-shrink-0">
                  <i className="ri-user-voice-line text-amber-400 text-base"></i>
                </div>
                <div>
                  <p className="text-white font-black text-sm" style={{ fontFamily: "'Cairo', sans-serif" }}>
                    {targets.length} مشترك سيتلقى الرسالة
                  </p>
                  <p className="text-gray-500 text-xs mt-0.5" style={{ fontFamily: "'Cairo', sans-serif" }}>
                    {selectedProductId === "all"
                      ? `من ${productGroups.length} منتج مختلف`
                      : productGroups.find((g) => g.product_id === selectedProductId)?.product_name}
                  </p>
                </div>
              </div>

              {/* ── Message Editor ── */}
              <div>
                <div className="flex items-center justify-between mb-2">
                  <label className="text-xs font-bold text-gray-400" style={{ fontFamily: "'Cairo', sans-serif" }}>
                    نص الرسالة
                  </label>
                  <div className="flex items-center gap-2">
                    <button
                      onClick={() => setMsgTemplate(DEFAULT_MSG_TEMPLATE(storeName))}
                      disabled={sending}
                      className="text-[10px] text-gray-500 hover:text-gray-300 transition-colors cursor-pointer whitespace-nowrap"
                      style={{ fontFamily: "'Cairo', sans-serif" }}
                    >
                      <i className="ri-refresh-line text-[10px] ml-0.5"></i>
                      إعادة تعيين
                    </button>
                    <span className={`text-[10px] ${charCount > 900 ? "text-red-400" : "text-gray-600"}`}
                      style={{ fontFamily: "'Cairo', sans-serif" }}>
                      {charCount}/1000
                    </span>
                  </div>
                </div>
                <textarea
                  value={msgTemplate}
                  onChange={(e) => setMsgTemplate(e.target.value.slice(0, 1000))}
                  disabled={sending}
                  rows={7}
                  className="w-full bg-[#111] border border-white/8 rounded-xl px-4 py-3 text-white text-sm outline-none focus:border-white/20 resize-none placeholder-gray-600 disabled:opacity-50"
                  style={{ fontFamily: "'Cairo', sans-serif" }}
                  placeholder="اكتب رسالتك هنا..."
                  dir="rtl"
                />
                <p className="text-gray-600 text-[10px] mt-1.5 leading-relaxed" style={{ fontFamily: "'Cairo', sans-serif" }}>
                  <i className="ri-information-line ml-1"></i>
                  سيُضاف اسم المنتج ورابطه تلقائياً في نهاية كل رسالة
                </p>
              </div>

              {/* ── Quick Templates ── */}
              <div>
                <p className="text-xs font-bold text-gray-500 mb-2" style={{ fontFamily: "'Cairo', sans-serif" }}>
                  قوالب سريعة
                </p>
                <div className="flex flex-col gap-2">
                  {[
                    {
                      label: "إعادة توفر",
                      icon: "ri-store-2-line",
                      msg: `🎉 *خبر سار!*\n\nالمنتج الذي كنت تنتظره عاد للمخزون!\n\nلا تفوّت الفرصة — الكميات محدودة.\n\n— ${storeName}`,
                    },
                    {
                      label: "عرض خاص",
                      icon: "ri-price-tag-3-line",
                      msg: `🔥 *عرض خاص للمشتركين!*\n\nبما أنك سجّلت اهتمامك بهذا المنتج، نقدم لك أولوية الشراء مع خصم حصري.\n\nتواصل معنا لمعرفة التفاصيل.\n\n— ${storeName}`,
                    },
                    {
                      label: "كمية محدودة",
                      icon: "ri-alarm-warning-line",
                      msg: `⚡ *تنبيه عاجل!*\n\nالمنتج الذي طلبت الإشعار عنه متوفر الآن بكميات محدودة جداً.\n\nاطلب الآن قبل النفاد!\n\n— ${storeName}`,
                    },
                  ].map((t) => (
                    <button
                      key={t.label}
                      onClick={() => setMsgTemplate(t.msg)}
                      disabled={sending}
                      className="flex items-center gap-3 bg-white/3 hover:bg-white/6 border border-white/8 rounded-xl px-4 py-3 text-right transition-all cursor-pointer disabled:opacity-40"
                    >
                      <div className="w-8 h-8 flex items-center justify-center bg-white/5 rounded-lg flex-shrink-0">
                        <i className={`${t.icon} text-gray-400 text-sm`}></i>
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-white text-xs font-bold" style={{ fontFamily: "'Cairo', sans-serif" }}>
                          {t.label}
                        </p>
                        <p className="text-gray-600 text-[10px] mt-0.5 truncate" style={{ fontFamily: "'Cairo', sans-serif" }}>
                          {t.msg.replace(/\*/g, "").split("\n")[0]}
                        </p>
                      </div>
                      <i className="ri-arrow-left-line text-gray-600 text-xs flex-shrink-0"></i>
                    </button>
                  ))}
                </div>
              </div>

              {/* ── Progress (while sending) ── */}
              {sending && (
                <div className="bg-[#111] border border-white/5 rounded-xl px-4 py-4">
                  <div className="flex items-center justify-between text-xs mb-2" style={{ fontFamily: "'Cairo', sans-serif" }}>
                    <span className="text-gray-400 flex items-center gap-1.5">
                      <i className="ri-loader-4-line animate-spin text-emerald-400"></i>
                      جاري الإرسال...
                    </span>
                    <span className="text-emerald-400 font-black">{sentCount} / {targets.length}</span>
                  </div>
                  <div className="h-2 bg-white/5 rounded-full overflow-hidden">
                    <div
                      className="h-full bg-gradient-to-r from-emerald-400 to-teal-500 rounded-full transition-all duration-500"
                      style={{ width: `${targets.length > 0 ? (sentCount / targets.length) * 100 : 0}%` }}
                    ></div>
                  </div>
                  {currentIdx >= 0 && currentIdx < targets.length && (
                    <p className="text-gray-600 text-[10px] mt-2 truncate" style={{ fontFamily: "'Cairo', sans-serif" }}>
                      يُرسل إلى: {targets[currentIdx]?.whatsapp || targets[currentIdx]?.phone}
                    </p>
                  )}
                </div>
              )}
            </>
          )}
        </div>

        {/* Footer */}
        <div className="px-6 py-4 border-t border-white/8 flex gap-3 flex-shrink-0">
          {done ? (
            <button
              onClick={onClose}
              className="flex-1 bg-emerald-500 hover:bg-emerald-600 text-white rounded-xl py-3 text-sm font-bold transition-colors cursor-pointer whitespace-nowrap"
              style={{ fontFamily: "'Cairo', sans-serif" }}
            >
              <i className="ri-check-line ml-1.5"></i>
              تم
            </button>
          ) : (
            <>
              <button
                onClick={onClose}
                disabled={sending}
                className="px-5 py-3 text-sm text-gray-500 hover:text-white transition-colors cursor-pointer whitespace-nowrap rounded-xl hover:bg-white/5 disabled:opacity-40"
                style={{ fontFamily: "'Cairo', sans-serif" }}
              >
                إلغاء
              </button>
              <button
                onClick={handleSend}
                disabled={sending || targets.length === 0 || !msgTemplate.trim()}
                className="flex-1 flex items-center justify-center gap-2 bg-emerald-500 hover:bg-emerald-600 disabled:opacity-40 disabled:cursor-not-allowed text-white rounded-xl py-3 text-sm font-bold transition-colors cursor-pointer whitespace-nowrap"
                style={{ fontFamily: "'Cairo', sans-serif" }}
              >
                {sending ? (
                  <>
                    <i className="ri-loader-4-line animate-spin text-sm"></i>
                    جاري الإرسال ({sentCount}/{targets.length})...
                  </>
                ) : (
                  <>
                    <i className="ri-send-plane-fill text-sm"></i>
                    إرسال لـ {targets.length} مشترك
                  </>
                )}
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

/* ══════════════════════════════════════════════════════════ */
/*                   MAIN COMPONENT                          */
/* ══════════════════════════════════════════════════════════ */

export default function StockNotificationsManager() {
  const [notifications, setNotifications] = useState<Notification[]>([]);
  const [loading, setLoading] = useState(true);
  const [activeView, setActiveView] = useState<"subscriptions" | "report">("subscriptions");
  const [filterStatus, setFilterStatus] = useState<"all" | "pending" | "notified">("pending");
  const [sendingId, setSendingId] = useState<number | null>(null);
  const [sendingProductId, setSendingProductId] = useState<number | null>(null);
  const [search, setSearch] = useState("");
  const [bulkModalOpen, setBulkModalOpen] = useState(false);
  const [bulkToast, setBulkToast] = useState("");
  const { settings } = useStoreSettings();

  const allPending = notifications.filter((n) => n.status === "pending");

  const fetchNotifications = useCallback(async () => {
    setLoading(true);
    const { data, error } = await supabase
      .from("stock_notifications")
      .select("*")
      .order("created_at", { ascending: false });
    if (!error && data) setNotifications(data as Notification[]);
    setLoading(false);
  }, []);

  useEffect(() => { fetchNotifications(); }, [fetchNotifications]);

  /* ── تحديث حالة مجموعة من الإشعارات بعد الإرسال الجماعي ── */
  const handleBulkUpdateStatus = (ids: number[]) => {
    const now = new Date().toISOString();
    setNotifications((prev) =>
      prev.map((n) =>
        ids.includes(n.id) ? { ...n, status: "notified", notified_at: now } : n
      )
    );
  };

  /* ── تصفية وتجميع ── */
  const filtered = notifications.filter((n) => {
    const matchStatus = filterStatus === "all" || n.status === filterStatus;
    const matchSearch =
      !search ||
      n.product_name.includes(search) ||
      n.phone.includes(search) ||
      n.whatsapp.includes(search);
    return matchStatus && matchSearch;
  });

  const grouped: GroupedProduct[] = Object.values(
    filtered.reduce((acc, n) => {
      if (!acc[n.product_id]) {
        acc[n.product_id] = { product_id: n.product_id, product_name: n.product_name, subscribers: [] };
      }
      acc[n.product_id].subscribers.push(n);
      return acc;
    }, {} as Record<number, GroupedProduct>)
  );

  /* ── إرسال إشعار فردي ── */
  const handleNotifyOne = async (notif: Notification) => {
    setSendingId(notif.id);
    try {
      const msg = encodeURIComponent(
        `🔔 *مرحباً!*\n\nالمنتج الذي طلبت الإشعار عنه أصبح متوفراً الآن:\n\n*${notif.product_name}*\n\nسارع بطلبه قبل نفاده: ${window.location.origin}/product/${notif.product_id}\n\n— ${settings.storeName}`
      );
      const phone = notif.whatsapp.replace(/^0/, "966");
      window.open(`https://wa.me/${phone}?text=${msg}`, "_blank", "noopener,noreferrer");

      await supabase
        .from("stock_notifications")
        .update({ status: "notified", notified_at: new Date().toISOString() })
        .eq("id", notif.id);

      setNotifications((prev) =>
        prev.map((n) =>
          n.id === notif.id ? { ...n, status: "notified", notified_at: new Date().toISOString() } : n
        )
      );
    } finally {
      setSendingId(null);
    }
  };

  /* ── إرسال إشعار لكل مشترك منتج ── */
  const handleNotifyAll = async (group: GroupedProduct) => {
    setSendingProductId(group.product_id);
    const pending = group.subscribers.filter((s) => s.status === "pending");
    for (const notif of pending) {
      await handleNotifyOne(notif);
    }
    setSendingProductId(null);
  };

  /* ── إلغاء اشتراك ── */
  const handleCancel = async (id: number) => {
    await supabase.from("stock_notifications").update({ status: "cancelled" }).eq("id", id);
    setNotifications((prev) => prev.map((n) => (n.id === id ? { ...n, status: "cancelled" } : n)));
  };

  const pendingCount = notifications.filter((n) => n.status === "pending").length;
  const notifiedCount = notifications.filter((n) => n.status === "notified").length;

  const statusColor = {
    pending: "bg-amber-400/15 text-amber-400 border-amber-400/25",
    notified: "bg-emerald-400/15 text-emerald-400 border-emerald-400/25",
    cancelled: "bg-gray-500/15 text-gray-500 border-gray-500/25",
  };
  const statusLabel = { pending: "بانتظار الإشعار", notified: "تم الإشعار", cancelled: "ملغي" };

  return (
    <div className="flex flex-col gap-6" dir="rtl">

      {/* Bulk Notify Modal */}
      {bulkModalOpen && (
        <BulkNotifyModal
          allPendingNotifications={allPending}
          storeName={settings.storeName}
          onClose={() => setBulkModalOpen(false)}
          onDone={(count) => {
            setBulkToast(`تم إشعار ${count} مشترك بنجاح ✓`);
            setTimeout(() => setBulkToast(""), 4000);
          }}
          onUpdateStatus={handleBulkUpdateStatus}
        />
      )}

      {/* Bulk Toast */}
      {bulkToast && (
        <div className="fixed bottom-6 left-1/2 -translate-x-1/2 z-50">
          <div className="bg-emerald-600 text-white text-sm px-5 py-3 rounded-xl flex items-center gap-2 whitespace-nowrap">
            <i className="ri-checkbox-circle-line text-emerald-200"></i>
            {bulkToast}
          </div>
        </div>
      )}

      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h2 className="text-white font-black text-xl" style={{ fontFamily: "'Cairo', sans-serif" }}>
            إشعارات نفاد المخزون
          </h2>
          <p className="text-gray-500 text-sm mt-1" style={{ fontFamily: "'Cairo', sans-serif" }}>
            العملاء المشتركون لمعرفة توفر المنتجات
          </p>
        </div>
        <div className="flex items-center gap-2">
          {/* Bulk Notify Button */}
          {allPending.length > 0 && (
            <button
              onClick={() => setBulkModalOpen(true)}
              className="flex items-center gap-2 px-4 py-2.5 rounded-xl bg-emerald-500/15 hover:bg-emerald-500/25 border border-emerald-500/30 text-emerald-400 text-sm font-bold transition-all cursor-pointer whitespace-nowrap"
              style={{ fontFamily: "'Cairo', sans-serif" }}
            >
              <i className="ri-send-plane-fill text-sm"></i>
              إشعار جماعي
              <span className="bg-emerald-500/20 border border-emerald-500/30 text-emerald-300 text-[10px] font-black px-1.5 py-0.5 rounded-full">
                {allPending.length}
              </span>
            </button>
          )}
          <button
            onClick={fetchNotifications}
            className="flex items-center gap-2 px-4 py-2 rounded-xl bg-white/5 border border-white/10 text-gray-400 text-sm hover:text-white transition-colors cursor-pointer whitespace-nowrap"
            style={{ fontFamily: "'Cairo', sans-serif" }}
          >
            <i className="ri-refresh-line"></i>
            تحديث
          </button>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
        {[
          { label: "إجمالي الطلبات", value: notifications.length, icon: "ri-notification-4-line", color: "text-white" },
          { label: "بانتظار الإشعار", value: pendingCount, icon: "ri-time-line", color: "text-amber-400" },
          { label: "تم الإشعار", value: notifiedCount, icon: "ri-checkbox-circle-line", color: "text-emerald-400" },
        ].map((s) => (
          <div key={s.label} className="bg-[#111] border border-white/5 rounded-2xl px-4 py-4 flex items-center gap-3">
            <div className="w-10 h-10 flex items-center justify-center bg-white/5 rounded-xl flex-shrink-0">
              <i className={`${s.icon} text-lg ${s.color}`}></i>
            </div>
            <div>
              <p className={`text-xl font-black ${s.color}`} style={{ fontFamily: "'Cairo', sans-serif" }}>{s.value}</p>
              <p className="text-gray-600 text-xs" style={{ fontFamily: "'Cairo', sans-serif" }}>{s.label}</p>
            </div>
          </div>
        ))}
      </div>

      {/* ── View Switcher ── */}
      <div className="flex gap-1 bg-[#111] border border-white/8 rounded-xl p-1 w-fit">
        {[
          { key: "subscriptions", label: "الاشتراكات", icon: "ri-notification-4-line" },
          { key: "report", label: "تقرير الطلب", icon: "ri-bar-chart-2-line" },
        ].map((v) => (
          <button
            key={v.key}
            onClick={() => setActiveView(v.key as "subscriptions" | "report")}
            className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-bold transition-all cursor-pointer whitespace-nowrap ${
              activeView === v.key
                ? "bg-white text-[#1a1a1a]"
                : "text-gray-500 hover:text-gray-300"
            }`}
            style={{ fontFamily: "'Cairo', sans-serif" }}
          >
            <i className={`${v.icon} text-sm`}></i>
            {v.label}
          </button>
        ))}
      </div>

      {/* ── Loading ── */}
      {loading ? (
        <div className="flex items-center justify-center py-16">
          <i className="ri-loader-4-line animate-spin text-2xl text-gray-600"></i>
        </div>
      ) : activeView === "report" ? (
        <DemandReport notifications={notifications} />
      ) : (
        <>
          {/* Filters */}
          <div className="flex flex-col sm:flex-row gap-3">
            <div className="relative flex-1">
              <i className="ri-search-line absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 text-sm pointer-events-none"></i>
              <input
                type="text"
                placeholder="بحث باسم المنتج أو رقم الجوال..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="w-full bg-[#111] border border-white/8 rounded-xl pr-9 pl-4 py-2.5 text-white text-sm outline-none focus:border-white/20 placeholder-gray-600"
                style={{ fontFamily: "'Cairo', sans-serif" }}
              />
            </div>
            <div className="flex gap-2">
              {(["all", "pending", "notified"] as const).map((s) => (
                <button
                  key={s}
                  onClick={() => setFilterStatus(s)}
                  className={`px-3 py-2 rounded-xl text-xs font-bold border transition-all cursor-pointer whitespace-nowrap ${
                    filterStatus === s
                      ? "bg-white/10 border-white/20 text-white"
                      : "bg-transparent border-white/8 text-gray-500 hover:text-gray-300"
                  }`}
                  style={{ fontFamily: "'Cairo', sans-serif" }}
                >
                  {s === "all" ? "الكل" : s === "pending" ? "بانتظار الإشعار" : "تم الإشعار"}
                </button>
              ))}
            </div>
          </div>

          {grouped.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-16 text-center">
              <div className="w-16 h-16 flex items-center justify-center bg-[#1a1a1a] rounded-full mb-4">
                <i className="ri-notification-off-line text-2xl text-gray-600"></i>
              </div>
              <p className="text-gray-500 text-sm" style={{ fontFamily: "'Cairo', sans-serif" }}>
                {filterStatus === "pending" ? "لا توجد اشتراكات بانتظار الإشعار" : "لا توجد نتائج"}
              </p>
            </div>
          ) : (
            <div className="flex flex-col gap-4">
              {grouped.map((group) => {
                const pendingInGroup = group.subscribers.filter((s) => s.status === "pending");
                return (
                  <div key={group.product_id} className="bg-[#111] border border-white/5 rounded-2xl overflow-hidden">
                    <div className="flex items-center justify-between px-5 py-4 border-b border-white/5">
                      <div className="flex items-center gap-3">
                        <div className="w-9 h-9 flex items-center justify-center bg-white/5 rounded-xl flex-shrink-0">
                          <i className="ri-box-3-line text-gray-400 text-base"></i>
                        </div>
                        <div>
                          <p className="text-white font-bold text-sm" style={{ fontFamily: "'Cairo', sans-serif" }}>
                            {group.product_name}
                          </p>
                          <p className="text-gray-500 text-xs mt-0.5" style={{ fontFamily: "'Cairo', sans-serif" }}>
                            {group.subscribers.length} مشترك — {pendingInGroup.length} بانتظار الإشعار
                          </p>
                        </div>
                      </div>
                      {pendingInGroup.length > 0 && (
                        <button
                          onClick={() => handleNotifyAll(group)}
                          disabled={sendingProductId === group.product_id}
                          className="flex items-center gap-1.5 bg-emerald-500/15 hover:bg-emerald-500/25 border border-emerald-500/25 text-emerald-400 text-xs font-bold px-3 py-2 rounded-xl transition-all cursor-pointer whitespace-nowrap disabled:opacity-50"
                          style={{ fontFamily: "'Cairo', sans-serif" }}
                        >
                          {sendingProductId === group.product_id ? (
                            <><i className="ri-loader-4-line animate-spin text-xs"></i> جاري الإرسال...</>
                          ) : (
                            <><i className="ri-whatsapp-line text-xs"></i> إشعار الكل ({pendingInGroup.length})</>
                          )}
                        </button>
                      )}
                    </div>

                    <div className="divide-y divide-white/5">
                      {group.subscribers.map((notif) => (
                        <div key={notif.id} className="flex items-center gap-3 px-5 py-3">
                          <div className="w-8 h-8 flex items-center justify-center bg-white/5 rounded-full flex-shrink-0">
                            <i className="ri-user-line text-gray-500 text-sm"></i>
                          </div>
                          <div className="flex-1 min-w-0">
                            <p className="text-white text-sm font-semibold" style={{ fontFamily: "'Cairo', sans-serif" }}>
                              {notif.whatsapp || notif.phone}
                            </p>
                            <p className="text-gray-600 text-[11px] mt-0.5" style={{ fontFamily: "'Cairo', sans-serif" }}>
                              سجّل في {new Date(notif.created_at).toLocaleString("ar-SA")}
                              {notif.notified_at && (
                                <span className="text-emerald-400/70 mr-2">
                                  · أُشعر في {new Date(notif.notified_at).toLocaleString("ar-SA")}
                                </span>
                              )}
                            </p>
                          </div>
                          <span className={`text-[10px] font-bold px-2.5 py-1 rounded-full border whitespace-nowrap ${statusColor[notif.status]}`}
                            style={{ fontFamily: "'Cairo', sans-serif" }}>
                            {statusLabel[notif.status]}
                          </span>
                          <div className="flex items-center gap-1.5">
                            {notif.status === "pending" && (
                              <button
                                onClick={() => handleNotifyOne(notif)}
                                disabled={sendingId === notif.id}
                                title="إرسال إشعار واتساب"
                                className="w-8 h-8 flex items-center justify-center bg-emerald-500/10 hover:bg-emerald-500/20 border border-emerald-500/20 text-emerald-400 rounded-lg transition-all cursor-pointer disabled:opacity-50"
                              >
                                {sendingId === notif.id ? (
                                  <i className="ri-loader-4-line animate-spin text-xs"></i>
                                ) : (
                                  <i className="ri-whatsapp-line text-xs"></i>
                                )}
                              </button>
                            )}
                            {notif.status === "pending" && (
                              <button
                                onClick={() => handleCancel(notif.id)}
                                title="إلغاء الاشتراك"
                                className="w-8 h-8 flex items-center justify-center bg-white/5 hover:bg-white/10 border border-white/8 text-gray-500 hover:text-gray-300 rounded-lg transition-all cursor-pointer"
                              >
                                <i className="ri-close-line text-xs"></i>
                              </button>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </>
      )}
    </div>
  );
}
